/**
 * FilesHome.js  -  /tasks/files
 *
 * The department folders this person may open, plus "Shared with my
 * department". The list comes from the server, which decides access from the
 * session: a folder that is not here was not sent, rather than sent and hidden.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Chip,
  Typography,
  Alert,
  AlertTitle,
  Skeleton,
  Container,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { tokens } from "../../../theme";
import { buildUi } from "../taskShared";
import { filesCall, fmtBytes } from "./filesApi";

import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

/**
 * One folder card. Module scope, not declared inside FilesHome: a component
 * defined inside another is a new type on every render, so React would
 * remount every card each time the page re-rendered.
 */
const FolderCard = ({ ui, title, subtitle, icon, accent, badge, onClick }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderRadius: "14px",
      p: 2.2,
      height: "100%",
      cursor: "pointer",
      boxShadow: ui.shadow,
      transition: "transform .15s ease, border-color .15s ease",
      "&:hover, &:focus-visible": {
        transform: "translateY(-2px)",
        borderColor: alpha(accent, 0.6),
        outline: "none",
      },
    }}
  >
    <Stack direction="row" spacing={1.6} alignItems="center">
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "12px",
          display: "grid",
          placeItems: "center",
          color: accent,
          bgcolor: alpha(accent, 0.14),
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="subtitle1"
            fontWeight={800}
            color={ui.textPrimary}
            noWrap
          >
            {title}
          </Typography>
          {badge}
        </Stack>
        <Typography variant="body2" color={ui.textSecondary} noWrap>
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  </Box>
);

const FilesHome = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await filesCall("roots");
    setLoading(false);
    if (!res?.success) {
      setError(res?.message || "Could not load your folders");
      return;
    }
    setData(res.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const me = data?.me;
  const folders = data?.folders || [];

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", py: 3 }}>
      <Container maxWidth="xl">
        {/* ---------------- Header ---------------- */}
        <Box
          sx={{
            background: ui.gradient,
            borderRadius: "16px",
            p: { xs: 2.5, md: 3.5 },
            mb: 3,
            color: "#fff",
            boxShadow: ui.shadow,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            gap={1.5}
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Files
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                Department folders and the files your colleagues have shared with you
              </Typography>
            </Box>
            {me && (
              <Chip
                icon={
                  me.full_access ? (
                    <AdminPanelSettingsOutlinedIcon sx={{ color: "#fff !important" }} />
                  ) : (
                    <FolderOutlinedIcon sx={{ color: "#fff !important" }} />
                  )
                }
                label={
                  me.full_access
                    ? "Management - full access to every folder"
                    : `${me.department || "No department"}`
                }
                sx={{ bgcolor: alpha("#fff", 0.2), color: "#fff", fontWeight: 700 }}
              />
            )}
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>
            {error}
          </Alert>
        )}

        {data?.unmatched_department && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: "12px" }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              Your department isn't linked to a folder
            </AlertTitle>
            Your staff record says{" "}
            <strong>{me?.department ? `"${me.department}"` : "no department"}</strong>,
            which doesn't match any of the department folders. Files shared with you
            will still appear below. Ask a manager to correct your department, or to
            link it to a folder.
          </Alert>
        )}

        {/* ---------------- Folders ---------------- */}
        <Grid container spacing={2.2}>
          {loading &&
            Array.from({ length: 4 }).map((_, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Skeleton variant="rounded" height={92} sx={{ borderRadius: "14px" }} />
              </Grid>
            ))}

          {!loading &&
            folders.map((f) => (
              <Grid key={f.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <FolderCard
                  ui={ui}
                  title={f.name}
                  accent={ui.accent}
                  icon={<FolderOutlinedIcon sx={{ fontSize: 28 }} />}
                  subtitle={`${f.file_count} file${f.file_count === 1 ? "" : "s"}${
                    f.total_bytes ? ` · ${fmtBytes(f.total_bytes)}` : ""
                  }${
                    f.subfolder_count
                      ? ` · ${f.subfolder_count} folder${f.subfolder_count === 1 ? "" : "s"}`
                      : ""
                  }`}
                  badge={
                    <>
                      {f.is_mine && (
                        <Chip
                          size="small"
                          label="Yours"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            color: ui.accent,
                            bgcolor: alpha(ui.accent, 0.14),
                          }}
                        />
                      )}
                      {f.is_system && (
                        <LockOutlinedIcon
                          titleAccess="Default folder - cannot be deleted"
                          sx={{ fontSize: 15, color: ui.textSecondary }}
                        />
                      )}
                    </>
                  }
                  onClick={() => navigate(`/tasks/files/${f.id}`)}
                />
              </Grid>
            ))}

          {!loading && data && (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FolderCard
                ui={ui}
                title="Shared with my department"
                accent="#8e24aa"
                icon={<FolderSharedOutlinedIcon sx={{ fontSize: 28 }} />}
                subtitle={`${data.shared_count} file${data.shared_count === 1 ? "" : "s"} from other departments`}
                onClick={() => navigate("/tasks/files/shared")}
              />
            </Grid>
          )}

          {/* Only drawn when the server says this person may open it. */}
          {!loading && data?.can_view_backup && (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FolderCard
                ui={ui}
                title="Backup"
                accent="#c62828"
                icon={<Inventory2OutlinedIcon sx={{ fontSize: 28 }} />}
                subtitle={`${data.backup_count} deleted file${data.backup_count === 1 ? "" : "s"} · activity log`}
                badge={
                  <LockOutlinedIcon
                    titleAccess="Top Management and management only"
                    sx={{ fontSize: 15, color: ui.textSecondary }}
                  />
                }
                onClick={() => navigate("/tasks/files/backup")}
              />
            </Grid>
          )}
        </Grid>

        {!loading && data && folders.length === 0 && !data.unmatched_department && (
          <Typography color={ui.textSecondary} sx={{ mt: 3 }}>
            No folders are available to you yet.
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default FilesHome;
